import ProgressivePromise from './ProgressivePromise'

export default function AsyncJobManager({ concurrency } = {}) {
  const state = {
    globalQueue: [],
    jobsStarted: 0,
    concurrency
  }

  const canAcceptJobs = () => !state.concurrency || (state.concurrency && state.jobsStarted < state.concurrency)

  const work = () => {
    if (state.globalQueue.length && canAcceptJobs()) {
      const currentJob = state.globalQueue.shift()
      if (currentJob.canceled) {
        currentJob.onDone('canceled', currentJob.index)
      } else {
        currentJob.onProgress('started', currentJob.index)
        state.jobsStarted++
        currentJob
          .resolve()
          .then(
            resolved => {
              currentJob.onDone('done', currentJob.index, resolved)
            },
            rejected => {
              currentJob.onDone('failed', currentJob.index, rejected)
            },
            (loaded, total, ...rest) => {
              currentJob.onProgress('progress', currentJob.index, loaded, total, ...rest)
            }
          )
          .finally(() => {
            currentJob.done = true
            state.jobsStarted--
            work()
          })
      }
      work()
    }
  }

  return {
    /**
     *
     * @param  {...ProgressivePromise} jobResolvers
     * @returns {ProgressivePromise}
     */
    queue(...jobResolvers) {
      return new ProgressivePromise((resolve, _reject, progress) => {
        if (!jobResolvers.length) {
          resolve([])
        }
        const results = Array.from({ length: jobResolvers.length })
        let jobsDone = 0
        state.globalQueue.push(
          ...jobResolvers.map((job, index) => {
            return {
              resolve: job,
              index,
              onDone: (status, jobIndex, result) => {
                results[jobIndex] = { status, result }
                jobsDone++
                progress('done', jobIndex, jobsDone, jobResolvers.length, status, result)
                if (jobsDone === jobResolvers.length) {
                  resolve(results)
                }
              },
              onProgress: progress,
              canceled: false,
              done: false
            }
          })
        )
        if (canAcceptJobs()) {
          work()
        }
      })
    },
    cancel(...jobResolvers) {
      for (let queuedJobIndex = 0; queuedJobIndex < state.globalQueue.length; queuedJobIndex++) {
        const queuedJob = state.globalQueue[queuedJobIndex]
        if (queuedJob.canceled) continue
        for (const jobResolver of jobResolvers) {
          if (queuedJob.resolve === jobResolver) {
            queuedJob.canceled = true
            break
          }
        }
      }
    },
    cancelAll() {
      for (let queuedJobIndex = 0; queuedJobIndex < state.globalQueue.length; queuedJobIndex++) {
        state.globalQueue[queuedJobIndex].canceled = true
      }
    },
    concurrency(newConcurrency) {
      state.concurrency = newConcurrency
      if (canAcceptJobs()) {
        work()
      }
    }
  }
}
